import { useEffect } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { apiClient } from 'api/ApiClient';
import type { UpdateCompanyRequest } from 'api/types';
import { useAuth } from 'shared/lib/auth/useAuth';
import { authDebug } from 'shared/lib/debug/authDebugger';

export function useGetCompany() {
  const { isAuthReady } = useAuth();

  // Only log when auth becomes ready (transitions to true)
  useEffect(() => {
    if (isAuthReady) {
      authDebug.log('useGetCompany', 'Auth is now ready, query enabled');
    }
  }, [isAuthReady]);

  return useQuery({
    queryKey: ['getCompany'],
    queryFn: () => {
      authDebug.log('useGetCompany', 'Executing /v1/company/ API call');
      return apiClient.getCompany();
    },
    enabled: isAuthReady, // Only run when auth is ready
  });
}

export function useGetTenantCompanies() {
  const { isAuthReady } = useAuth();

  // Only log when auth becomes ready (transitions to true)
  useEffect(() => {
    if (isAuthReady) {
      authDebug.log('useGetTenantCompanies', 'Auth is now ready, query enabled');
    }
  }, [isAuthReady]);

  return useQuery({
    queryKey: ['getTenantCompanies'],
    queryFn: () => {
      authDebug.log('useGetTenantCompanies', 'Executing /v1/companies/ API call');
      return apiClient.getTenantCompanies();
    },
    enabled: isAuthReady, // Only run when auth is ready
  });
}

export function useGetCompanySettings() {
  const { user } = useAuth();
  const id = user?.company?.id;

  return useQuery({
    queryKey: ['getCompanySettings', id],
    queryFn: () => (id ? apiClient.getCompanySettings(id) : Promise.reject()),
    enabled: !!id,
  });
}

export function useUpdateCompanySettings() {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const client = useQueryClient();
  const id = user?.company?.id;

  const { mutate, isPending } = useMutation({
    mutationKey: ['updateCompanySettings'],
    mutationFn: (data: UpdateCompanyRequest) => (id ? apiClient.updateCompanySettings(id, data) : Promise.reject()),
    onMutate: () =>
      client.invalidateQueries({
        queryKey: ['getCompany'],
      }),
    onSuccess: (data) => {
      enqueueSnackbar('Your changes have been saved');

      client.setQueryData(['getCompanySettings', id], () => {
        return data;
      });
    },
  });

  return {
    update: mutate,
    isLoading: isPending,
  };
}
